import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { LucideModule } from '../../../shared/modules/lucide.module';
import { PageCategoriaServicosCardComponent } from '../page-categoria-servicos/page-categoria-servicos-card/page-categoria-servicos-card.component';
import { PaginacaoComponent } from '../../../shared/components/paginacao/paginacao.component';
import {
  CategoriaServicoDigital,
  ServicoDigitalItem,
  ServicosDigitaisService,
} from '../services/servicos-digitais.service';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { InputBuscarComponent } from '../../../shared/components/input-buscar/input-buscar.component';
import { LinksDinamicosComponent } from '../links-dinamicos/links-dinamicos.component';
import { ScrollNavegacaoService } from '../../../core/services/scroll-navegacao.service';

const ACENTOS = /\p{Diacritic}/gu;

const SERVICOS_POR_PAGINA = 12;

function normalizar(valor: string): string {
  return valor.normalize('NFD').replace(ACENTOS, '').toLowerCase().trim();
}

/**
 * A tela "Serviços Digitais": os serviços com atendimento `Solicitação Online` de todas as
 * categorias, com filtro por categoria e busca por nome ou resumo — ambos no cliente, sobre
 * a lista inteira que o `ServicosDigitaisService` deriva do catálogo público.
 */
@Component({
  selector: 'app-servicos-digitais',
  templateUrl: './servicos-digitais.component.html',
  imports: [
    LinksDinamicosComponent,
    InputBuscarComponent,
    BreadcrumbComponent,
    LucideModule,
    PageCategoriaServicosCardComponent,
    PaginacaoComponent,
  ],
  host: {
    '(document:click)': 'aoClicarForaDoSelect($event)',
    '(document:keydown.escape)': 'fecharSelectComFoco()',
  },
})
export class ServicosDigitaisComponent {
  private readonly servicosDigitaisService = inject(ServicosDigitaisService);
  private readonly injector = inject(Injector);
  private readonly scrollNavegacao = inject(ScrollNavegacaoService);

  private readonly listaServicos = viewChild<ElementRef<HTMLElement>>('listaServicos');
  private readonly selectCategorias = viewChild<ElementRef<HTMLElement>>('selectCategorias');
  private readonly selectCategoriasControle =
    viewChild<ElementRef<HTMLButtonElement>>('selectCategoriasControle');

  readonly selectCategoriasAberto = signal(false);
  readonly termoBusca = signal('');
  readonly categoriasSelecionadas = signal<ReadonlySet<string>>(new Set());

  readonly servicosDigitais = this.servicosDigitaisService.servicosDigitais;
  readonly categorias = this.servicosDigitaisService.categorias;
  readonly carregando = this.servicosDigitaisService.carregando;
  readonly falhou = this.servicosDigitaisService.falhou;

  /** O órgão não tem nenhum serviço digital publicado — diferente de "nada casa com o filtro". */
  readonly semCatalogo = computed(
    () => !this.carregando() && !this.falhou() && this.servicosDigitais().length === 0
  );

  readonly resumoCategorias = computed(() => {
    const selecionadas = this.categorias().filter((categoria) =>
      this.categoriasSelecionadas().has(categoria.id)
    );

    if (selecionadas.length === 0) return 'Todas as categorias';
    if (selecionadas.length === 1) return selecionadas[0].titulo;

    return `${selecionadas.length} categorias selecionadas`;
  });

  readonly servicosFiltrados = computed<readonly ServicoDigitalItem[]>(() => {
    const termo = normalizar(this.termoBusca());
    const categorias = this.categoriasSelecionadas();

    return this.servicosDigitais().filter(
      (servico) =>
        this.combinaComCategoria(servico, categorias) && this.combinaComTermo(servico, termo)
    );
  });

  readonly totalResultados = computed(() => this.servicosFiltrados().length);

  readonly totalPaginas = computed(() =>
    Math.max(1, Math.ceil(this.totalResultados() / SERVICOS_POR_PAGINA))
  );

  readonly paginaAtual = linkedSignal({
    source: () => ({ termo: this.termoBusca(), categorias: this.categoriasSelecionadas() }),
    computation: () => 1,
  });

  readonly servicosDaPagina = computed<readonly ServicoDigitalItem[]>(() => {
    const inicio = (this.paginaAtual() - 1) * SERVICOS_POR_PAGINA;

    return this.servicosFiltrados().slice(inicio, inicio + SERVICOS_POR_PAGINA);
  });

  readonly temFiltroAtivo = computed(
    () => !!this.termoBusca() || this.categoriasSelecionadas().size > 0
  );

  irParaPagina(pagina: number): void {
    this.paginaAtual.set(pagina);

    afterNextRender(() => this.rolarParaLista(), { injector: this.injector });
  }

  /** O "Tentar novamente" do aviso de falha. */
  recarregar(): void {
    this.servicosDigitaisService.recarregar();
  }

  aoBuscar(evento: Event): void {
    this.termoBusca.set((evento.target as HTMLInputElement).value);
  }

  limparBusca(): void {
    this.termoBusca.set('');
  }

  alternarCategoria(id: string): void {
    this.categoriasSelecionadas.update((atual) => {
      const proximo = new Set(atual);
      if (!proximo.delete(id)) {
        proximo.add(id);
      }
      return proximo;
    });
  }

  limparCategorias(): void {
    this.categoriasSelecionadas.set(new Set());
  }

  limparTudo(): void {
    this.limparBusca();
    this.limparCategorias();
  }

  alternarSelectCategorias(): void {
    this.selectCategoriasAberto.update((aberto) => !aberto);
  }

  fecharSelectComFoco(): void {
    if (!this.selectCategoriasAberto()) return;

    this.selectCategoriasAberto.set(false);
    this.selectCategoriasControle()?.nativeElement.focus();
  }

  aoClicarForaDoSelect(evento: Event): void {
    if (!this.selectCategoriasAberto()) return;

    const alvo = evento.target as Node | null;
    if (alvo && this.selectCategorias()?.nativeElement.contains(alvo)) return;

    this.selectCategoriasAberto.set(false);
  }

  categoriaSelecionada(id: string): boolean {
    return this.categoriasSelecionadas().has(id);
  }

  /**
   * O nome acessível da caixa de seleção. Explícito porque o texto do `<label>` junta o nome
   * e o total sem separador ("Obras2") — o template não preserva o espaço entre os spans.
   */
  rotuloOpcaoCategoria(categoria: CategoriaServicoDigital): string {
    const unidade = categoria.total === 1 ? 'serviço digital' : 'serviços digitais';

    return `${categoria.titulo}, ${categoria.total} ${unidade}`;
  }

  private rolarParaLista(): void {
    this.scrollNavegacao.aoElemento(this.listaServicos()?.nativeElement);
  }

  /** Sem seleção passa tudo; com seleção, basta o serviço estar em UMA das categorias. */
  private combinaComCategoria(
    servico: ServicoDigitalItem,
    categorias: ReadonlySet<string>
  ): boolean {
    return categorias.size === 0 || servico.categoriaIds.some((id) => categorias.has(id));
  }

  private combinaComTermo(servico: ServicoDigitalItem, termo: string): boolean {
    if (!termo) return true;

    const alvo = normalizar(`${servico.titulo} ${servico.descricao}`);

    return alvo.includes(termo);
  }
}
